import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  RefreshCw, Loader, AlertCircle, Play, Pause, RotateCcw, X,
  ChevronRight, TrendingUp, Wallet, BadgePercent, Info, CheckSquare, Square
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';

/**
 * Partner Client Portfolio (v2 — dormant model)
 * ---------------------------------------------
 * Lists every client account the partner has linked from their MCC. Each
 * client is either dormant (visible but not billed) or active (counted on
 * the Stripe subscription). Partners decide explicitly which clients they
 * want to bill — there is no trial period.
 *
 * The component drives `/api/partner/portfolio` plus per-client mutation
 * endpoints. Activations show a confirmation modal first, with the resolved
 * tier, monthly fee and a pro-rated estimate for the running month.
 */

const TIER_COLORS = {
  starter:    '#3b82f6',
  growth:     '#10b981',
  scale:      '#8b5cf6',
  enterprise: '#ec4899'
};

const STATUS_CONFIG = {
  dormant:   { label: 'Inactive',  color: '#6b7280' },
  active:    { label: 'Active',    color: '#10b981' },
  paused:    { label: 'Paused',    color: '#f59e0b' },
  cancelled: { label: 'Cancelled', color: '#ef4444' }
};

function formatEuros(cents) {
  if (cents === null || cents === undefined) return '—';
  const value = (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
  return `€${value}`;
}

function formatSpend(cents) {
  if (!cents) return '€0';
  const euros = cents / 100;
  if (euros >= 1000) return `€${(euros / 1000).toFixed(1)}k`;
  return `€${euros.toFixed(0)}`;
}

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#6b7280' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 9px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: `${cfg.color}1f`,
      color: cfg.color,
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    }}>{cfg.label}</span>
  );
}

function TierPill({ tier, isOverride }) {
  const color = TIER_COLORS[tier] || '#6b7280';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 9px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: `${color}1f`,
      color: color,
      textTransform: 'capitalize',
      border: isOverride ? `1px dashed ${color}` : 'none'
    }}>
      {tier}{isOverride ? ' · manual' : ''}
    </span>
  );
}

function BillingSummaryBanner({ summary, theme }) {
  const empty = summary.activeBilled === 0;

  return (
    <div style={{
      background: empty
        ? theme.colors.bg.secondary
        : `linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(59,130,246,0.10) 100%)`,
      border: `1px solid ${empty ? theme.colors.border.primary : 'rgba(16,185,129,0.3)'}`,
      borderRadius: 12,
      padding: 18,
      marginBottom: 16
    }}>
      {empty ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <Info size={18} color={theme.colors.text.tertiary} style={{ marginTop: 2 }} />
          <div>
            <div style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              No active billing yet
            </div>
            <div style={{ color: theme.colors.text.secondary, fontSize: 13, lineHeight: 1.5 }}>
              Linked client accounts start as <strong>inactive</strong>. Activate the
              ones you want to manage in Bidoryx — billing starts immediately at the
              tier matching their previous-month ad spend. Your first activation
              brings you to Stripe Checkout to enter your payment details.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 16,
            marginBottom: 14
          }}>
            <div>
              <div style={{ color: theme.colors.text.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Active clients
              </div>
              <div style={{ color: theme.colors.text.primary, fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                {summary.activeBilled}
              </div>
            </div>
            <div>
              <div style={{ color: theme.colors.text.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Subtotal
              </div>
              <div style={{ color: theme.colors.text.primary, fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                {formatEuros(summary.subtotalCents)}
              </div>
            </div>
            <div>
              <div style={{ color: theme.colors.text.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Volume discount
              </div>
              <div style={{ color: '#10b981', fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                −{formatEuros(summary.discountCents)}
              </div>
              {summary.bracket && (
                <div style={{ color: theme.colors.text.muted, fontSize: 11, marginTop: 2 }}>
                  {summary.discountPercent}% · {summary.bracket.label}
                </div>
              )}
            </div>
            <div>
              <div style={{ color: theme.colors.text.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total / month
              </div>
              <div style={{ color: theme.colors.text.primary, fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                {formatEuros(summary.totalCents)}
              </div>
            </div>
          </div>

          {summary.nextBracket && (
            <div style={{
              padding: '10px 12px',
              background: theme.colors.bg.tertiary,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 13,
              color: theme.colors.text.secondary
            }}>
              <BadgePercent size={15} color="#8b5cf6" />
              <span>
                Add <strong style={{ color: theme.colors.text.primary }}>
                  {summary.nextBracket.extraClientsNeeded}
                </strong> more active client{summary.nextBracket.extraClientsNeeded > 1 ? 's' : ''} to
                unlock{' '}
                <strong style={{ color: '#8b5cf6' }}>
                  {summary.nextBracket.percentOff}% volume discount
                </strong>
                {' '}(at {summary.nextBracket.atClientCount}+ clients).
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ClientRow({ client, theme, onAction, busyAction, selectable, selected, onToggleSelect }) {
  const tierColor = TIER_COLORS[client.currentTier] || '#6b7280';
  const isBusy = busyAction === client.id;

  // Minimal action menu per status. Owner-only — backend enforces too.
  let primaryAction = null;
  if (client.billingStatus === 'dormant') {
    primaryAction = { key: 'activate', label: 'Activate billing', icon: Play, color: '#10b981' };
  } else if (client.billingStatus === 'paused') {
    primaryAction = { key: 'resume', label: 'Resume billing', icon: RotateCcw, color: '#10b981' };
  } else if (client.billingStatus === 'active') {
    primaryAction = { key: 'pause', label: 'Pause billing', icon: Pause, color: '#f59e0b' };
  }

  const checkboxAvailable = selectable && client.billingStatus === 'dormant';

  return (
    <div style={{
      background: theme.colors.bg.secondary,
      border: `1px solid ${selected ? '#10b981' : theme.colors.border.primary}`,
      borderRadius: 10,
      padding: '12px 14px',
      display: 'grid',
      gridTemplateColumns: '32px 1.5fr 110px 100px 130px 130px auto',
      gap: 14,
      alignItems: 'center'
    }}>
      {/* Bulk-select checkbox (only visible for dormant rows) */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {checkboxAvailable ? (
          <button
            onClick={() => onToggleSelect(client.id)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: selected ? '#10b981' : theme.colors.text.tertiary,
              display: 'flex'
            }}
            title={selected ? 'Deselect' : 'Select for bulk activate'}
          >
            {selected ? <CheckSquare size={18} /> : <Square size={18} />}
          </button>
        ) : <span />}
      </div>

      {/* Name + customer id */}
      <div style={{ minWidth: 0 }}>
        <div style={{
          color: theme.colors.text.primary,
          fontSize: 14,
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {client.accountName || 'Unnamed account'}
        </div>
        <div style={{ color: theme.colors.text.muted, fontSize: 11, marginTop: 2 }}>
          {client.customerId}
        </div>
      </div>

      {/* Status */}
      <div>
        <StatusPill status={client.billingStatus} />
      </div>

      {/* Tier */}
      <div>
        <TierPill tier={client.currentTier} isOverride={!!client.manualTierOverride} />
      </div>

      {/* Previous-month spend */}
      <div>
        <div style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 600 }}>
          {formatSpend(client.previousMonthSpendCents)}
        </div>
        <div style={{ color: theme.colors.text.muted, fontSize: 10 }}>
          last month spend
        </div>
      </div>

      {/* Monthly fee — preview for dormant, actual for active */}
      <div>
        <div style={{ color: tierColor, fontSize: 13, fontWeight: 700 }}>
          {formatEuros(client.monthlyFeeCents)}
        </div>
        <div style={{ color: theme.colors.text.muted, fontSize: 10 }}>
          {client.billingStatus === 'dormant' ? 'if activated' : 'per month'}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {primaryAction && (
          <button
            disabled={isBusy}
            onClick={() => onAction(client, primaryAction.key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px',
              background: primaryAction.color,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: isBusy ? 'not-allowed' : 'pointer',
              opacity: isBusy ? 0.6 : 1
            }}
          >
            {isBusy
              ? <Loader size={13} className="spin" />
              : <primaryAction.icon size={13} />}
            {primaryAction.label}
          </button>
        )}
        {client.billingStatus !== 'cancelled' && (
          <button
            disabled={isBusy}
            onClick={() => onAction(client, 'menu')}
            style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '7px 9px',
              background: 'transparent',
              color: theme.colors.text.tertiary,
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: 8,
              fontSize: 12,
              cursor: 'pointer'
            }}
            title="More actions"
          >
            <ChevronRight size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function ClientActionMenu({ client, theme, onAction, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: theme.colors.bg.primary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 12,
          padding: 18,
          maxWidth: 380,
          width: '100%'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 700 }}>
              {client.accountName || client.customerId}
            </div>
            <div style={{ color: theme.colors.text.muted, fontSize: 12 }}>
              Manage this client
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none',
            color: theme.colors.text.tertiary, cursor: 'pointer'
          }}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={() => {
              const tier = window.prompt(
                'Set manual tier override (starter / growth / scale / enterprise) or empty to clear:',
                client.manualTierOverride || ''
              );
              if (tier === null) return;
              const value = tier.trim().toLowerCase();
              const valid = ['starter', 'growth', 'scale', 'enterprise', ''];
              if (!valid.includes(value)) {
                alert('Invalid tier. Use starter, growth, scale, enterprise or leave empty.');
                return;
              }
              onAction(client, 'override-tier', { tier: value || null });
            }}
            style={menuButtonStyle(theme)}
          >
            <TrendingUp size={15} /> Override tier (manual)
          </button>
          {client.billingStatus !== 'cancelled' && (
            <button
              onClick={() => {
                if (window.confirm(
                  'Cancel billing for this client? It is removed from the subscription immediately. ' +
                  'Stripe credits any remaining pro-rata back to your customer balance. The account stays linked.'
                )) {
                  onAction(client, 'cancel');
                }
              }}
              style={{ ...menuButtonStyle(theme), color: '#ef4444' }}
            >
              <X size={15} /> Cancel billing
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Confirmation modal shown before activating a single client. Loads the
 * preview from the backend (resolved tier, monthly fee, projected bracket,
 * pro-rated estimate) so the partner can confirm exactly what they're
 * signing up for. The actual mutation is invoked by `onConfirm`.
 */
function ActivationConfirmModal({ client, theme, onConfirm, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiCall(
          `/api/partner/portfolio/clients/${client.id}/preview-activation`
        );
        const json = await res.json();
        if (!alive) return;
        if (!json.success) throw new Error(json.error || 'Could not load preview');
        setPreview(json.preview);
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [client.id]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };

  const tierColor = TIER_COLORS[preview?.client?.currentTier] || theme.colors.text.primary;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 110, padding: 20
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: theme.colors.bg.primary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 14,
          padding: 22,
          maxWidth: 480,
          width: '100%'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div style={{ color: theme.colors.text.primary, fontSize: 17, fontWeight: 700 }}>
              Activate billing
            </div>
            <div style={{ color: theme.colors.text.muted, fontSize: 12, marginTop: 2 }}>
              {client.accountName || client.customerId}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none',
            color: theme.colors.text.tertiary, cursor: 'pointer'
          }}><X size={18} /></button>
        </div>

        {loading && (
          <div style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
            <Loader size={18} className="spin" /> Calculating preview…
          </div>
        )}

        {error && (
          <div style={{
            padding: 12,
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8,
            color: '#ef4444',
            fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {preview && !loading && (
          <>
            <div style={{
              background: theme.colors.bg.secondary,
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: 10,
              padding: 14,
              marginBottom: 14
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ color: theme.colors.text.muted, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Tier
                </span>
                <TierPill tier={preview.client.currentTier} isOverride={false} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ color: theme.colors.text.muted, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Last month spend
                </span>
                <span style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 600 }}>
                  {formatSpend(preview.client.previousMonthSpendCents)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: theme.colors.text.muted, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Monthly fee
                </span>
                <span style={{ color: tierColor, fontSize: 18, fontWeight: 700 }}>
                  {formatEuros(preview.client.monthlyFeeCents)}
                </span>
              </div>
            </div>

            <div style={{
              background: theme.colors.bg.tertiary,
              borderRadius: 10,
              padding: 14,
              marginBottom: 14
            }}>
              <div style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Pro-rated charge for the rest of this month
              </div>
              <div style={{ color: theme.colors.text.secondary, fontSize: 13, lineHeight: 1.5 }}>
                ~ <strong style={{ color: theme.colors.text.primary }}>€{preview.proration.estimateEuros}</strong>{' '}
                ({preview.proration.daysRemaining} of {preview.proration.daysInMonth} days
                {preview.proration.discountPct > 0 && (
                  <>, incl. {preview.proration.discountPct}% volume discount</>
                )}
                ). Stripe handles the exact proration on your next invoice.
              </div>
            </div>

            {preview.bracket?.changes && preview.bracket?.projected && (
              <div style={{
                padding: 12,
                background: 'rgba(139, 92, 246, 0.08)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: 8,
                fontSize: 12,
                color: theme.colors.text.secondary,
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 14
              }}>
                <BadgePercent size={14} color="#8b5cf6" />
                <span>
                  This activation unlocks the{' '}
                  <strong style={{ color: '#8b5cf6' }}>
                    {preview.bracket.projected.label}
                  </strong>{' '}
                  bracket — applied automatically to all active clients.
                </span>
              </div>
            )}

            {preview.client.isFirstActivation && (
              <div style={{
                padding: 12,
                background: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: 8,
                fontSize: 12,
                color: theme.colors.text.secondary,
                marginBottom: 14
              }}>
                <strong style={{ color: theme.colors.text.primary }}>First activation:</strong>{' '}
                you'll be redirected to Stripe Checkout to enter your payment details. After
                that, all future activations are one-click.
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '9px 16px',
              background: 'transparent',
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: 8,
              color: theme.colors.text.secondary,
              fontSize: 13,
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || submitting || error}
            style={{
              padding: '9px 18px',
              background: '#10b981',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              cursor: (loading || submitting || error) ? 'not-allowed' : 'pointer',
              opacity: (loading || submitting || error) ? 0.6 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 7
            }}
          >
            {submitting ? <Loader size={13} className="spin" /> : <Play size={13} />}
            {preview?.client?.isFirstActivation ? 'Continue to Stripe' : 'Activate billing'}
          </button>
        </div>
      </div>
    </div>
  );
}

function menuButtonStyle(theme) {
  return {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px',
    background: theme.colors.bg.secondary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: 8,
    color: theme.colors.text.primary,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'left'
  };
}

export default function PartnerPortfolio() {
  const { theme } = useTheme();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [reclassifying, setReclassifying] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [activationCandidate, setActivationCandidate] = useState(null); // single-activate confirm
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActivating, setBulkActivating] = useState(false);
  const [billingHealth, setBillingHealth] = useState(null);
  const [completingSetup, setCompletingSetup] = useState(false);

  const fetchPortfolio = useCallback(async () => {
    try {
      setError(null);
      const res = await apiCall('/api/partner/portfolio');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Could not load portfolio');
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  // Diagnose Stripe configuration so a misconfigured price/coupon (or wrong
  // Stripe mode) surfaces as a clear banner instead of clients silently going
  // "active" without ever being billed.
  const fetchBillingHealth = useCallback(async () => {
    try {
      const res = await apiCall('/api/partner/portfolio/billing-health');
      const json = await res.json();
      if (json.success) setBillingHealth(json);
    } catch (err) {
      // Non-blocking diagnostic — ignore failures.
    }
  }, []);

  useEffect(() => {
    fetchBillingHealth();
  }, [fetchBillingHealth]);

  // Recovery for clients that are marked active but whose org never completed
  // Stripe Checkout (e.g. activated under the old silent-failure behaviour, or
  // an abandoned checkout). Triggers a sync and redirects to the checkout URL.
  const handleCompleteBillingSetup = async () => {
    setCompletingSetup(true);
    try {
      const res = await apiCall('/api/partner/portfolio/sync', { method: 'POST' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Could not sync billing');
      if (json.needsCheckout && json.checkoutUrl) {
        window.location.href = json.checkoutUrl;
        return;
      }
      showToast('Billing is now set up in Stripe', 'success');
      await fetchPortfolio();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setCompletingSetup(false);
    }
  };

  const handleReclassify = async () => {
    setReclassifying(true);
    try {
      const res = await apiCall('/api/partner/portfolio/reclassify', { method: 'POST' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Reclassify failed');
      showToast(`Reclassified ${json.processed} client${json.processed === 1 ? '' : 's'}`, 'success');
      await fetchPortfolio();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setReclassifying(false);
    }
  };

  const performAction = async (clientId, actionKey, body = {}) => {
    setBusyAction(clientId);
    const endpoints = {
      'activate':      `/api/partner/portfolio/clients/${clientId}/activate`,
      'pause':         `/api/partner/portfolio/clients/${clientId}/pause`,
      'resume':        `/api/partner/portfolio/clients/${clientId}/resume`,
      'cancel':        `/api/partner/portfolio/clients/${clientId}/cancel`,
      'override-tier': `/api/partner/portfolio/clients/${clientId}/override-tier`
    };
    try {
      const res = await apiCall(endpoints[actionKey], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Action failed');

      if (json.stripe?.needsCheckout && json.stripe?.checkoutUrl) {
        window.location.href = json.stripe.checkoutUrl;
        return;
      }

      const successMessages = {
        'activate':      'Billing activated',
        'pause':         'Billing paused',
        'resume':        'Billing resumed',
        'cancel':        'Billing cancelled',
        'override-tier': 'Tier override updated'
      };
      showToast(successMessages[actionKey] || 'Updated', 'success');
      await fetchPortfolio();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusyAction(null);
    }
  };

  const handleAction = async (client, actionKey, body = {}) => {
    if (actionKey === 'menu') {
      setMenuFor(client);
      return;
    }
    setMenuFor(null);

    // Activation needs the confirmation modal first.
    if (actionKey === 'activate') {
      setActivationCandidate(client);
      return;
    }

    await performAction(client.id, actionKey, body);
  };

  const handleConfirmedActivate = async () => {
    if (!activationCandidate) return;
    const client = activationCandidate;
    setActivationCandidate(null);
    await performAction(client.id, 'activate');
  };

  const handleBulkActivate = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkActivating(true);
    try {
      const res = await apiCall('/api/partner/portfolio/activate-bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientIds: ids })
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Bulk activate failed');

      // First activation across the whole org → redirect to Stripe Checkout.
      if (json.stripe?.needsCheckout && json.stripe?.checkoutUrl) {
        window.location.href = json.stripe.checkoutUrl;
        return;
      }

      showToast(
        `Activated ${json.activated} client${json.activated === 1 ? '' : 's'}` +
        (json.failed > 0 ? ` (${json.failed} failed)` : ''),
        json.failed > 0 ? 'error' : 'success'
      );
      setSelectedIds(new Set());
      await fetchPortfolio();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBulkActivating(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const sortedClients = useMemo(() => {
    if (!data?.clients) return [];
    // Order: dormant first (so partner sees what's not yet activated up top),
    // then active, then paused, then cancelled. Within each bucket: name asc.
    const order = { dormant: 0, active: 1, paused: 2, cancelled: 3 };
    return [...data.clients].sort((a, b) => {
      const oa = order[a.billingStatus] ?? 99;
      const ob = order[b.billingStatus] ?? 99;
      if (oa !== ob) return oa - ob;
      return (a.accountName || '').localeCompare(b.accountName || '');
    });
  }, [data]);

  const dormantCount = useMemo(
    () => sortedClients.filter(c => c.billingStatus === 'dormant').length,
    [sortedClients]
  );

  const selectAllDormant = () => {
    setSelectedIds(new Set(
      sortedClients.filter(c => c.billingStatus === 'dormant').map(c => c.id)
    ));
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
        <Loader size={20} className="spin" /> Loading portfolio…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: 16,
        background: 'rgba(239,68,68,0.1)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: 10,
        color: '#ef4444',
        display: 'flex', alignItems: 'center', gap: 10
      }}>
        <AlertCircle size={18} /> {error}
      </div>
    );
  }

  if (!data) return null;

  const { summary } = data;

  return (
    <div style={{
      background: theme.colors.bg.primary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12,
      padding: 18,
      marginTop: 16
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 14
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Wallet size={16} color={theme.colors.text.tertiary} />
            <span style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 600 }}>
              Client Portfolio Billing
            </span>
          </div>
          <div style={{ color: theme.colors.text.muted, fontSize: 12, marginTop: 4 }}>
            {summary.totalLinked} linked · {summary.activeBilled} active ·{' '}
            {summary.dormant ?? 0} inactive · {summary.paused} paused
          </div>
        </div>

        <button
          onClick={handleReclassify}
          disabled={reclassifying}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 12px',
            background: theme.colors.bg.secondary,
            color: theme.colors.text.primary,
            border: `1px solid ${theme.colors.border.primary}`,
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: reclassifying ? 'not-allowed' : 'pointer'
          }}
        >
          {reclassifying ? <Loader size={13} className="spin" /> : <RefreshCw size={13} />}
          {reclassifying ? 'Reclassifying…' : 'Refresh tiers'}
        </button>
      </div>

      {billingHealth && billingHealth.stripeConfigured && !billingHealth.healthy && (
        <div style={{
          padding: '12px 14px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10,
          marginBottom: 12,
          fontSize: 13,
          color: theme.colors.text.primary
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#ef4444', marginBottom: 6 }}>
            <AlertCircle size={16} /> Stripe billing is not fully configured ({billingHealth.mode} mode)
          </div>
          <div style={{ color: theme.colors.text.secondary, marginBottom: 8 }}>
            Clients can be marked active, but they won't be charged until the missing Stripe objects below
            exist in <strong>{billingHealth.mode}</strong> mode. Activation now fails loudly instead of silently.
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: theme.colors.text.secondary, lineHeight: 1.6 }}>
            {billingHealth.prices?.filter(p => !p.found).map(p => (
              <li key={p.lookupKey}>
                Missing price for tier <strong>{p.tier}</strong> — Stripe lookup_key <code>{p.lookupKey}</code>
              </li>
            ))}
            {billingHealth.coupons?.filter(c => !c.found).map(c => (
              <li key={c.couponId}>
                Missing coupon <code>{c.couponId}</code> ({c.label})
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.org && !data.org.stripeSubscriptionId && summary.activeBilled > 0
        && (!billingHealth || billingHealth.healthy) && (
        <div style={{
          padding: '12px 14px',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 10,
          marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'
        }}>
          <div style={{ fontSize: 13, color: theme.colors.text.secondary }}>
            <strong style={{ color: '#f59e0b' }}>Billing not yet active in Stripe.</strong>{' '}
            {summary.activeBilled} client{summary.activeBilled === 1 ? '' : 's'} {summary.activeBilled === 1 ? 'is' : 'are'} marked
            active but there's no Stripe subscription yet, so nothing is being charged. Complete the
            one-time billing setup to start invoicing.
          </div>
          <button
            onClick={handleCompleteBillingSetup}
            disabled={completingSetup}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '9px 14px', background: '#f59e0b', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: completingSetup ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap'
            }}
          >
            {completingSetup ? <Loader size={14} className="spin" /> : <Wallet size={14} />}
            {completingSetup ? 'Opening Stripe…' : 'Complete billing setup'}
          </button>
        </div>
      )}

      <BillingSummaryBanner summary={summary} theme={theme} />

      {/* Bulk actions toolbar — only relevant when there's at least 2 dormants. */}
      {dormantCount >= 2 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px',
          background: theme.colors.bg.tertiary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 10,
          marginBottom: 10,
          fontSize: 13
        }}>
          <div style={{ color: theme.colors.text.secondary, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>
              <strong style={{ color: theme.colors.text.primary }}>{selectedIds.size}</strong> of {dormantCount} inactive selected
            </span>
            {selectedIds.size === 0 ? (
              <button
                onClick={selectAllDormant}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: '#8b5cf6', fontSize: 12, fontWeight: 600, padding: 0
                }}
              >
                Select all inactive
              </button>
            ) : (
              <button
                onClick={() => setSelectedIds(new Set())}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: theme.colors.text.tertiary, fontSize: 12, fontWeight: 600, padding: 0
                }}
              >
                Clear selection
              </button>
            )}
          </div>
          <button
            onClick={handleBulkActivate}
            disabled={selectedIds.size === 0 || bulkActivating}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '7px 14px',
              background: selectedIds.size === 0 ? theme.colors.bg.secondary : '#10b981',
              color: selectedIds.size === 0 ? theme.colors.text.tertiary : '#fff',
              border: selectedIds.size === 0 ? `1px solid ${theme.colors.border.primary}` : 'none',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              cursor: (selectedIds.size === 0 || bulkActivating) ? 'not-allowed' : 'pointer'
            }}
          >
            {bulkActivating ? <Loader size={13} className="spin" /> : <Play size={13} />}
            Activate selected
          </button>
        </div>
      )}

      {sortedClients.length === 0 ? (
        <div style={{
          padding: 28,
          textAlign: 'center',
          color: theme.colors.text.muted,
          fontSize: 13
        }}>
          No client accounts linked yet. Use <strong>"Sync MCC accounts"</strong> to import
          them — each new client lands as <strong>inactive</strong>. You explicitly choose
          who to activate billing for.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '32px 1.5fr 110px 100px 130px 130px auto',
            gap: 14,
            padding: '0 14px',
            color: theme.colors.text.muted,
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            <div></div>
            <div>Account</div>
            <div>Status</div>
            <div>Tier</div>
            <div>Spend</div>
            <div>Fee</div>
            <div></div>
          </div>
          {sortedClients.map(client => (
            <ClientRow
              key={client.id}
              client={client}
              theme={theme}
              busyAction={busyAction}
              onAction={handleAction}
              selectable={dormantCount >= 2}
              selected={selectedIds.has(client.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

      {menuFor && (
        <ClientActionMenu
          client={menuFor}
          theme={theme}
          onAction={handleAction}
          onClose={() => setMenuFor(null)}
        />
      )}

      {activationCandidate && (
        <ActivationConfirmModal
          client={activationCandidate}
          theme={theme}
          onConfirm={handleConfirmedActivate}
          onClose={() => setActivationCandidate(null)}
        />
      )}
    </div>
  );
}
